import React, { useEffect, useState, useRef } from 'react';
import Lottie from 'lottie-react';
import { ThemeBackground } from '../services/themeService';

interface BackgroundMediaProps {
  background?: ThemeBackground;
}

export const BackgroundMedia: React.FC<BackgroundMediaProps> = ({ background }) => {
  const [lottieData, setLottieData] = useState<any>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const lottieRef = useRef<any>(null);
  const [isTabVisible, setIsTabVisible] = useState(!document.hidden);

  // Optimisation: Gérer visibilité onglet pour pause/play Lottie
  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = !document.hidden;
      setIsTabVisible(visible);

      if (lottieRef.current) {
        if (visible) {
          lottieRef.current.play();
        } else {
          lottieRef.current.pause();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    if (background?.type === 'lottie' && background.data) {
      try {
        const parsed = JSON.parse(background.data);
        setLottieData(parsed);
      } catch (e) {
        // Invalid Lottie data
      }
    } else {
      setLottieData(null);
    }

    setImageLoaded(false);
    setImageError(false);
  }, [background?.type, background?.data, background?.opacity, background?.blur]);

  if (!background || background.type === 'none' || !background.data) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 0 }}
    >
      {background.type === 'image' && (
        <>
          <img
            src={background.data}
            alt="Background"
            className="w-full h-full object-cover"
            style={{
              opacity: background.opacity || 0.3,
              filter: `blur(${background.blur || 0}px)`,
            }}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
        </>
      )}
      {background.type === 'video' && (
        <video
          src={background.data}
          className="w-full h-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          style={{
            opacity: background.opacity || 0.3,
            filter: `blur(${background.blur || 0}px)`,
          }}
        />
      )}
      {background.type === 'lottie' && lottieData && (
        <div
          className="w-full h-full flex items-center justify-center"
          style={{
            opacity: background.opacity || 0.3,
            filter: `blur(${background.blur || 0}px)`,
          }}
        >
          <Lottie
            lottieRef={lottieRef}
            animationData={lottieData}
            loop={true}
            autoplay={isTabVisible}
            isPaused={!isTabVisible}
            speed={0.7}
            rendererSettings={{
              preserveAspectRatio: 'xMidYMid slice',
              progressiveLoad: true,
            }}
            style={{ width: '100%', height: '100%' }}
          />
        </div>
      )}
    </div>
  );
};
